import { useEffect, useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
export interface PendingImage {
  id: string;
  claimToken: string;
  preview: string;
}
export function useImageAttachments(scope: string | number) {
  const [images, setImages] = useState<PendingImage[]>([]),
    [uploading, setUploading] = useState(false),
    [error, setError] = useState("");
  const current = useRef(images),
    busy = useRef(false),
    generation = useRef(0);
  current.current = images;
  function clear() {
    generation.current++;
    setUploading(false);
    for (const image of current.current) URL.revokeObjectURL(image.preview);
    current.current = [];
    setImages([]);
    setError("");
  }
  useEffect(() => {
    generation.current++;
    setUploading(false);
    clear();
    return () => {
      generation.current++;
      for (const image of current.current) URL.revokeObjectURL(image.preview);
    };
  }, [scope]);
  async function addFiles(files: FileList | File[]) {
    if (busy.current) return;
    busy.current = true;
    setUploading(true);
    setError("");
    const epoch = generation.current;
    try {
      for (const file of Array.from(files)) {
        if (current.current.length >= 3)
          throw Error("Attach up to three images per message.");
        if (file.size > 8 * 1024 * 1024)
          throw Error("Images must be 8 MB or smaller.");
        if (
          !/^image\/(jpeg|png|webp|heic|heif)$/.test(file.type) &&
          !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)
        )
          throw Error("Choose a JPEG, PNG, WebP or HEIC image.");
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onerror = () => reject(Error("Could not read this image."));
          reader.onload = () => resolve(String(reader.result).split(",")[1]!);
          reader.readAsDataURL(file);
        });
        const token = localStorage.getItem("ayrshire_token");
        const res = await fetch("/api/message-images", {
          method: "POST",
          headers: {
            "Content-Type": "text/plain",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: base64,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok)
          throw Error(
            data.error ?? "Could not upload this image. Please try again.",
          );
        if (epoch !== generation.current) return;
        const next = {
          id: data.id as string,
          claimToken: data.claimToken as string,
          preview: URL.createObjectURL(file),
        };
        current.current = [...current.current, next];
        setImages(current.current);
      }
    } catch (e) {
      if (epoch === generation.current)
        setError(e instanceof Error ? e.message : "Image upload failed.");
    } finally {
      busy.current = false;
      if (epoch === generation.current) setUploading(false);
    }
  }
  function remove(id: string) {
    const image = current.current.find((i) => i.id === id);
    if (image) URL.revokeObjectURL(image.preview);
    current.current = current.current.filter((i) => i.id !== id);
    setImages(current.current);
  }
  return {
    images,
    uploading,
    error,
    addFiles,
    remove,
    clear,
    claims: images.map(({ id, claimToken }) => ({ id, claimToken })),
  };
}
export function ImageAttachmentPicker({
  state,
  disabled = false,
}: {
  state: ReturnType<typeof useImageAttachments>;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="space-y-2">
      <input
        ref={input}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        className="sr-only"
        aria-label="Choose screenshot or photo"
        disabled={disabled || state.uploading}
        onChange={(e) => {
          if (e.target.files) void state.addFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        onClick={() => input.current?.click()}
        disabled={disabled || state.uploading || state.images.length >= 3}
        className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 disabled:opacity-50"
      >
        <ImagePlus size={18} />
        {state.uploading ? "Uploading image…" : "Attach screenshot or photo"}
      </button>
      {state.error && (
        <p role="alert" className="text-sm text-red-600">
          {state.error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {state.images.map((image) => (
          <div key={image.id} className="relative">
            <img
              src={image.preview}
              alt="Selected attachment"
              className="h-20 w-24 rounded-lg object-cover"
            />
            <button
              type="button"
              disabled={disabled}
              aria-label="Remove image"
              onClick={() => state.remove(image.id)}
              className="absolute right-0 top-0 rounded-full bg-slate-900 p-2 text-white"
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
function LoadedPrivateImage({ id }: { id: string }) {
  const [url, setUrl] = useState(""),
    [error, setError] = useState(false),
    [open, setOpen] = useState(false),
    [actualSize, setActualSize] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = "";
    setUrl("");
    setError(false);
    const token = localStorage.getItem("ayrshire_token");
    void fetch(`/api/message-images/${id}`, {
      headers: { Authorization: `Bearer ${token ?? ""}` },
      signal: controller.signal,
    })
      .then(async (r) => {
        if (!r.ok) throw Error();
        const blob = await r.blob();
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);
  if (error)
    return <p className="text-xs">Image unavailable. Refresh to try again.</p>;
  if (!url)
    return (
      <p className="text-xs" role="status">
        Loading image…
      </p>
    );
  return (
    <>
      <button
        type="button"
        onClick={() => {setActualSize(false);setOpen(true);}}
        aria-label="Open image full size"
        className="block max-w-full"
      >
        <img
          src={url}
          alt="Message attachment"
          className="max-h-64 max-w-full rounded-lg object-contain"
        />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl" aria-describedby={undefined}>
          <DialogTitle>Image attachment</DialogTitle>
          <button type="button" onClick={()=>setActualSize(value=>!value)} aria-pressed={actualSize} className="min-h-11 rounded-lg border px-3 py-2 text-sm">{actualSize?"Fit to screen":"View actual size"}</button>
          <div className="overflow-auto max-h-[80dvh]">
            <img
              src={url}
              alt="Full-size message attachment"
              className="h-auto"
              style={{maxWidth:actualSize?"none":"100%"}}
            />
          </div>
          <a href={url} download="screenshot.jpg" className="text-sm underline">
            Save image
          </a>
        </DialogContent>
      </Dialog>
    </>
  );
}
function PrivateImage({id}:{id:string}) {
  const element=useRef<HTMLDivElement>(null),[visible,setVisible]=useState(false);
  useEffect(()=>{
    if(!element.current)return;
    if(typeof IntersectionObserver==="undefined"){setVisible(true);return;}
    const observer=new IntersectionObserver(entries=>{if(entries.some(entry=>entry.isIntersecting)){setVisible(true);observer.disconnect();}},{rootMargin:"250px"});
    observer.observe(element.current);return()=>observer.disconnect();
  },[]);
  return <div ref={element} className="min-h-16 max-w-full">{visible?<LoadedPrivateImage id={id}/>:<p className="text-xs">Image attachment</p>}</div>;
}
export function MessageImages({ ids }: { ids?: string[] }) {
  return ids?.length ? (
    <div className="my-2 flex max-w-full flex-col gap-2">
      {ids.map((id) => (
        <PrivateImage key={id} id={id} />
      ))}
    </div>
  ) : null;
}
/** Image-only send for the existing order chat composers; normal text replies remain unchanged. */
export function ChatImageComposer({
  endpoint,
  recipientUserId,
  bodyKey = "body",
  onSent,
}: {
  endpoint: string;
  recipientUserId?: number | null;
  bodyKey?: "text" | "body";
  onSent?: () => void | Promise<void>;
}) {
  const state = useImageAttachments(endpoint + ":" + recipientUserId),
    [open, setOpen] = useState(false),
    [sending, setSending] = useState(false),
    [error, setError] = useState("");
  async function send() {
    if (sending || state.uploading || !state.images.length) return;
    setSending(true);
    setError("");
    try {
      const token = localStorage.getItem("ayrshire_token");
      const r = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token ?? ""}`,
        },
        body: JSON.stringify({
          [bodyKey]: "Sent an image",
          attachments: state.claims,
          ...(recipientUserId ? { recipientUserId } : {}),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok)
        throw Error(data.error ?? "Could not send images. Please try again.");
      state.clear();
      setOpen(false);
      await onSent?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send images.");
    } finally {
      setSending(false);
    }
  }
  return (
    <>
      <button
        type="button"
        aria-label="Send screenshot or photo"
        onClick={() => setOpen(true)}
        className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg border border-slate-300"
      >
        <ImagePlus size={20} />
      </button>
      <Dialog
        open={open}
        onOpenChange={(v) => {
          if (!sending && !state.uploading) {
            setOpen(v);
            if (!v) state.clear();
          }
        }}
      >
        <DialogContent>
          <DialogTitle>Send screenshots or photos</DialogTitle>
          <ImageAttachmentPicker state={state} disabled={sending} />
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <button
            type="button"
            disabled={!state.images.length || sending || state.uploading}
            onClick={() => void send()}
            className="min-h-11 rounded-lg bg-primary px-4 text-white disabled:opacity-50"
          >
            {sending ? "Sending…" : "Send images"}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
